import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Modal, Dropdown } from 'react-bootstrap';
import { User, Mail, Phone, MapPin, Edit2, Lock, Shield, Heart, Award, CreditCard, Building, Hash, ChevronDown, CheckCircle } from 'lucide-react';
import '../../../../assets/css/donor/dashboard/DonorProfile.css';
import DonorLayout from '../../../../layouts/donor/DonorLayout';
import AppContext from "../../../../context/AppContext";

const DonorProfile = () => {
  const { baseUrl, regExpChecker } = useContext(AppContext);
  // Profile states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");

  // Errors
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [addressError, setAddressError] = useState("");

  // Password states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Bank States
  const [accountTitle, setAccountTitle] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [branchCode, setBranchCode] = useState("");
  const [iban, setIban] = useState("");
  const [accountType, setAccountType] = useState("Current");

  // Errors
  const [accountTitleError, setAccountTitleError] = useState("");
  const [accountNumberError, setAccountNumberError] = useState("");
  const [bankNameError, setBankNameError] = useState("");
  const [ibanError, setIbanError] = useState("");

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeCause, setActiveCause] = useState('education');

  // Profile data
  const profileData = {
    name: 'Ali Hassan',
    email: 'ali.hassan@email.com',
    phone: '+92-300-1234567',
    address: 'Gujranwala, Punjab, Pakistan',
    donorType: 'Individual',
    profilePicture: 'https://ui-avatars.com/api/?name=Ali+Hassan&background=10183f&color=fff&size=150',
    totalDonated: 45000,
    numberOfDonations: 25,
    lastDonationDate: '12-Jan-2026',
    memberSince: 'March 2024',
    level: 'Gold Donor'
  };

  // Bank details data
  const bankData = {
    accountTitle: 'Ali Hassan',
    accountNumber: '1234-5678-9012-3456',
    bankName: 'HBL - Habib Bank Limited',
    branchCode: '0123',
    iban: 'PK36HABB0000001123456702',
    accountType: 'Current'
  };

  // All rewards — NGO-assigned level is 'Gold' (active)
  const dpAllRewards = [
    {
      id: 1,
      level: 'Bronze',
      reward: '🏅 Badge',
      ngo: 'Helping NGO A',
      amount: 'PKR 25,000',
      date: '12 Feb 2024',
      status: 'Issued',
      isActive: false,
    },
    {
      id: 2,
      level: 'Silver',
      reward: '📜 Certificate',
      ngo: 'Charity Org B',
      amount: 'PKR 50,000',
      date: '05 Jan 2024',
      status: 'Issued',
      isActive: false,
    },
    {
      id: 3,
      level: 'Gold',
      reward: '🏆 Trophy',
      ngo: 'EduCare NGO',
      amount: 'PKR 45,000',
      date: '10 Mar 2025',
      status: 'Issued',
      isActive: true, // NGO assigned — this is active level
    },
    {
      id: 4,
      level: 'Platinum',
      reward: '💎 Platinum Award',
      ngo: '—',
      amount: 'PKR 300,000',
      date: '—',
      status: 'Locked',
      isActive: false,
    },
  ];

  // Active reward — NGO assigned
  const dpActiveReward = dpAllRewards.find((r) => r.isActive);

  // Preferred causes
  const causes = [
    { id: 'education', label: 'Education', icon: 'books' },
    { id: 'fee', label: 'Fee', icon: 'money' },
    { id: 'books', label: 'Books', icon: 'book' },
    { id: 'uniform', label: 'Uniform', icon: 'shirt' },
  ];

  useEffect(() => {
    const savedCause = localStorage.getItem('preferredCause');
    if (savedCause) setActiveCause(savedCause);
  }, []);

  const handleCauseSelect = (causeId) => {
    if (isEditMode) {
      setActiveCause(causeId);
      localStorage.setItem('preferredCause', causeId);
    }
  };

  const handleEditClick = () => setIsEditMode(!isEditMode);

  const renderIcon = (iconType) => {
    switch (iconType) {
      case 'books':
        return (
          <div className="pfc-books-icon">
            <div className="pfc-book pfc-book-1"></div>
            <div className="pfc-book pfc-book-2"></div>
            <div className="pfc-book pfc-book-3"></div>
          </div>
        );
      case 'money': return <div className="pfc-emoji-icon">💰</div>;
      case 'book': return <div className="pfc-emoji-icon">📚</div>;
      case 'shirt': return <div className="pfc-emoji-icon">👕</div>;
      default: return null;
    }
  };

  const getLevelClass = (level) => {
    const map = { Bronze: 'dp-level-bronze', Silver: 'dp-level-silver', Gold: 'dp-level-gold', Platinum: 'dp-level-platinum' };
    return map[level] || 'dp-level-silver';
  };

  return (
    <DonorLayout>
      <div className="donor-profile-container">
        <Container fluid>

          {/* ── Profile Header Card ── */}
          <Row className="mb-4">
            <Col>
              <Card className="donor-profile-header-card">
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={2} className="text-center mb-3 mb-md-0">
                      <div className="donor-profile-picture-wrapper">
                        <img
                          src={profileData.profilePicture}
                          alt="Profile"
                          className="donor-profile-picture"
                        />
                        {/* Level badge — NGO assigned level with dropdown */}
                        <Dropdown className="dp-level-dropdown-wrap">
                          <Dropdown.Toggle as="div" className="donor-profile-level-badge dp-level-toggle">
                            <Award size={14} />
                            <span>{dpActiveReward?.level} Donor</span>
                            <ChevronDown size={12} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="dp-rewards-dropdown-menu">
                            <div className="dp-dropdown-title">All Rewards</div>
                            {dpAllRewards.map((r) => (
                              <div key={r.id} className={`dp-reward-dropdown-item ${r.isActive ? 'dp-reward-active' : ''} ${r.status === 'Locked' ? 'dp-reward-locked' : ''}`}>
                                <div className="dp-reward-row-top">
                                  <span className={`dp-level-pill ${getLevelClass(r.level)}`}>{r.level}</span>
                                  {r.isActive && (
                                    <span className="dp-ngo-assigned-tag">
                                      <CheckCircle size={11} /> NGO Assigned
                                    </span>
                                  )}
                                  {r.status === 'Locked' && (
                                    <span className="dp-locked-tag">🔒 Locked</span>
                                  )}
                                </div>
                                <div className="dp-reward-row-mid">
                                  <span className="dp-reward-name">{r.reward}</span>
                                  <span className="dp-reward-amount">{r.amount}</span>
                                </div>
                                {r.ngo !== '—' && (
                                  <div className="dp-reward-ngo">by {r.ngo} · {r.date}</div>
                                )}
                              </div>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </Col>

                    <Col md={7} className="mb-3 mb-md-0">
                      <h2 className="donor-profile-name">{profileData.name}</h2>
                      <div className="donor-profile-meta">
                        <Badge bg="primary" className="donor-profile-type-badge">
                          <User size={14} className="me-1" />
                          {profileData.donorType}
                        </Badge>
                        <span className="donor-profile-location">
                          <MapPin size={14} className="me-1" />
                          {profileData.address}
                        </span>
                      </div>
                      <p className="donor-profile-member-since">
                        Member since {profileData.memberSince}
                      </p>
                    </Col>

                    <Col md={3} className="text-md-end">
                      <Button className="donor-profile-edit-btn" onClick={() => setShowEditModal(true)}>
                        <Edit2 size={18} />
                        Edit Profile
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Personal Information ── */}
          <Row className="mb-4">
            <Col>
              <Card className="donor-profile-info-card">
                <Card.Body>
                  <h4 className="donor-profile-section-title">
                    <User size={20} className="donor-profile-section-icon" />
                    Personal Information
                  </h4>
                  <div className="donor-profile-info-grid">
                    <div className="donor-profile-info-item">
                      <div className="donor-profile-info-icon"><User size={18} /></div>
                      <div className="donor-profile-info-content">
                        <label className="donor-profile-info-label">Full Name</label>
                        <p className="donor-profile-info-value">{profileData.name}</p>
                      </div>
                    </div>
                    <div className="donor-profile-info-item">
                      <div className="donor-profile-info-icon"><Mail size={18} /></div>
                      <div className="donor-profile-info-content">
                        <label className="donor-profile-info-label">Email</label>
                        <p className="donor-profile-info-value">{profileData.email}</p>
                      </div>
                    </div>
                    <div className="donor-profile-info-item">
                      <div className="donor-profile-info-icon"><Phone size={18} /></div>
                      <div className="donor-profile-info-content">
                        <label className="donor-profile-info-label">Phone</label>
                        <p className="donor-profile-info-value">{profileData.phone}</p>
                      </div>
                    </div>
                    <div className="donor-profile-info-item">
                      <div className="donor-profile-info-icon"><MapPin size={18} /></div>
                      <div className="donor-profile-info-content">
                        <label className="donor-profile-info-label">Address</label>
                        <p className="donor-profile-info-value">{profileData.address}</p>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Bank Details Section (NEW) ── */}
          <Row className="mb-4">
            <Col>
              <Card className="dp-bank-card">
                <Card.Body>
                  <div className="dp-bank-header">
                    <h4 className="donor-profile-section-title mb-0">
                      <CreditCard size={20} className="donor-profile-section-icon" />
                      Bank Details
                    </h4>
                    <Button className="dp-bank-edit-btn" onClick={() => setShowBankModal(true)}>
                      <Edit2 size={15} /> Update Bank Info
                    </Button>
                  </div>

                  <div className="dp-bank-grid">
                    <div className="dp-bank-item">
                      <div className="dp-bank-icon dp-bank-icon-navy"><User size={16} /></div>
                      <div className="dp-bank-content">
                        <label className="dp-bank-label">Account Title</label>
                        <p className="dp-bank-value">{bankData.accountTitle}</p>
                      </div>
                    </div>
                    <div className="dp-bank-item">
                      <div className="dp-bank-icon dp-bank-icon-orange"><Hash size={16} /></div>
                      <div className="dp-bank-content">
                        <label className="dp-bank-label">Account Number</label>
                        <p className="dp-bank-value dp-bank-mono">{bankData.accountNumber}</p>
                      </div>
                    </div>
                    <div className="dp-bank-item">
                      <div className="dp-bank-icon dp-bank-icon-navy"><Building size={16} /></div>
                      <div className="dp-bank-content">
                        <label className="dp-bank-label">Bank Name</label>
                        <p className="dp-bank-value">{bankData.bankName}</p>
                      </div>
                    </div>
                    <div className="dp-bank-item">
                      <div className="dp-bank-icon dp-bank-icon-orange"><Hash size={16} /></div>
                      <div className="dp-bank-content">
                        <label className="dp-bank-label">Branch Code</label>
                        <p className="dp-bank-value dp-bank-mono">{bankData.branchCode}</p>
                      </div>
                    </div>
                    <div className="dp-bank-item dp-bank-item-full">
                      <div className="dp-bank-icon dp-bank-icon-navy"><CreditCard size={16} /></div>
                      <div className="dp-bank-content">
                        <label className="dp-bank-label">IBAN</label>
                        <p className="dp-bank-value dp-bank-mono">{bankData.iban}</p>
                      </div>
                    </div>
                    <div className="dp-bank-item">
                      <div className="dp-bank-icon dp-bank-icon-orange"><Shield size={16} /></div>
                      <div className="dp-bank-content">
                        <label className="dp-bank-label">Account Type</label>
                        <p className="dp-bank-value">{bankData.accountType}</p>
                      </div>
                    </div>
                  </div>

                  <div className="dp-bank-notice">
                    <Shield size={14} />
                    <span>Your bank details are encrypted and used only for donation refunds or payouts.</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Preferred Causes ── */}
          <Row className="mb-4">
            <Col>
              <Card className="donor-profile-causes-card">
                <Card.Body>
                  <div className="pfc-header">
                    <h4 className="pfc-title">
                      <svg className="pfc-title-icon" viewBox="0 0 24 24">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                      </svg>
                      Preferred Causes
                    </h4>
                    <Button
                      variant="outline-secondary"
                      className={`pfc-edit-btn ${isEditMode ? 'pfc-editing' : ''}`}
                      onClick={handleEditClick}
                    >
                      <svg className="pfc-edit-icon" viewBox="0 0 24 24">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      {isEditMode ? 'Save' : 'Edit'}
                    </Button>
                  </div>

                  <Row className="pfc-causes-grid">
                    {causes.map((cause) => (
                      <Col key={cause.id} xs={12} sm={6} md={6} lg={3} className="pfc-cause-col">
                        <div
                          className={`pfc-cause-card ${activeCause === cause.id ? 'pfc-active' : ''} ${isEditMode ? 'pfc-editable' : ''}`}
                          onClick={() => handleCauseSelect(cause.id)}
                          role="button"
                          tabIndex={0}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCauseSelect(cause.id); }
                          }}
                        >
                          <div className="pfc-checkmark">
                            <svg className="pfc-checkmark-icon" viewBox="0 0 24 24">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                          <div className="pfc-icon-wrapper">{renderIcon(cause.icon)}</div>
                          <div className="pfc-cause-label">{cause.label}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <p className="pfc-info-text">
                    Only one cause can be active at a time.
                    {isEditMode && <span className="pfc-edit-hint"> Click on a card to change your preference.</span>}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Account Settings ── */}
          <Row className="mb-4">
            <Col>
              <Card className="donor-profile-settings-card">
                <Card.Body>
                  <h4 className="donor-profile-section-title">
                    <Lock size={20} className="donor-profile-section-icon" />
                    Account Settings
                  </h4>
                  <div className="donor-profile-settings-buttons">
                    <Button className="donor-profile-settings-btn" onClick={() => setShowPasswordModal(true)}>
                      <Lock size={18} /> Change Password
                    </Button>
                    <Button className="donor-profile-settings-btn" onClick={() => alert('Privacy settings will be implemented')}>
                      <Shield size={18} /> Privacy Settings
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </Container>

        {/* ── Change Password Modal ── */}
        <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
          <Modal.Header closeButton className="donor-profile-modal-header">
            <Modal.Title>Change Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Current Password</Form.Label>
                <Form.Control type="password" placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value)
                    setCurrentPasswordError(regExpChecker("password", e.target.value));
                  }}
                />
                <p style={{ color: "red" }}>{currentPasswordError}</p>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control type="password" placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setNewPasswordError(regExpChecker("password", e.target.value));
                  }}
                />
                <p style={{ color: "red" }}>{newPasswordError}</p>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control type="password" placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setConfirmPasswordError(regExpChecker("confirmPassword", e.target.value));
                  }}
                />
                <p style={{ color: "red" }}>{confirmPasswordError}</p>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button className="donor-profile-modal-save-btn" onClick={() => setShowPasswordModal(false)}>Save Changes</Button>
          </Modal.Footer>
        </Modal>

        {/* ── Edit Profile Modal ── */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton className="donor-profile-modal-header">
            <Modal.Title>Edit Profile</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control type="text"
                  Value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameError(regExpChecker("username", e.target.value));
                  }}
                />
                <p style={{ color: "red", fontSize: "12px" }}>{nameError}</p>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email"
                  Value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(regExpChecker("email", e.target.value));
                  }}
                />
                <p style={{ color: "red", fontSize: "12px" }}>{emailError}</p>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control type="tel"
                  Value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setPhoneError(regExpChecker("phone", e.target.value));
                  }}
                />
                <p style={{ color: "red", fontSize: "12px" }}>{phoneError}</p>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control type="text"
                  Value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setAddressError(regExpChecker("string", e.target.value));
                  }}
                />
                <p style={{ color: "red", fontSize: "12px" }}>{addressError}</p>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button className="donor-profile-modal-save-btn" onClick={() => setShowEditModal(false)}>Save Changes</Button>
          </Modal.Footer>
        </Modal>

        {/* ── Update Bank Info Modal ── */}
        <Modal show={showBankModal} onHide={() => setShowBankModal(false)} centered size="lg">
          <Modal.Header closeButton className="donor-profile-modal-header">
            <Modal.Title>Update Bank Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>Account Title</Form.Label>
                  <Form.Control type="text"
                    value={accountTitle}
                    onChange={(e) => {
                      setAccountTitle(e.target.value);
                      setAccountTitleError(regExpChecker("string", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{accountTitleError}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Account Number</Form.Label>
                  <Form.Control type="text"
                    value={accountNumber}
                    onChange={(e) => {
                      setAccountNumber(e.target.value);
                      setAccountNumberError(regExpChecker("number", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{accountNumberError}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control type="text"
                    value={bankName}
                    onChange={(e) => {
                      setBankName(e.target.value);
                      setBankNameError(regExpChecker("string", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{bankNameError}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Branch Code</Form.Label>
                  <Form.Control type="text"

                    value={branchCode}
                    onChange={(e) => setBranchCode(e.target.value)}
                  />
                </Col>
                <Col md={8} className="mb-3">
                  <Form.Label>IBAN</Form.Label>
                  <Form.Control type="text"
                    value={iban}
                    onChange={(e) => {
                      setIban(e.target.value);
                      setIbanError(regExpChecker("iban", e.target.value));
                    }}
                  />
                  <p style={{ color: "red" }}>{ibanError}</p>
                </Col>
                <Col md={4} className="mb-3">
                  <Form.Label>Account Type</Form.Label>
                  <Form.Select

                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                  >

                    <option>Current</option>
                    <option>Savings</option>
                  </Form.Select>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBankModal(false)}>Cancel</Button>
            <Button className="donor-profile-modal-save-btn" onClick={() => setShowBankModal(false)}>Save Bank Details</Button>
          </Modal.Footer>
        </Modal>

      </div>
    </DonorLayout>
  );
};

export default DonorProfile;